import React from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { Formik, Form, FormikValues } from 'formik';
import * as Yup from 'yup';

import { Wrap } from '../styled';

import { H3, Paragraph } from '@components/Typography';
import { Button } from '@components/Buttons/Button';

import PasswordField from '@components/FormElements/FormikFields/Password';
import { FieldsGroup } from '@components/FormElements/styled';
import { addNotificationActionCreator } from '@store/notifications/actions';
import { NotificationType } from '@store/notifications/types';

import history from '@utils/history';
import RESET_PASSWORD from '@graphql/mutations/resetPassword';
import { getInvalidCharsMessage } from '@components/Auth/InvalidCharsMessage';

const ConfirmPassword = () => {
  const { token } = useParams<{ token: string }>();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [resetPassword, { loading }] = useMutation(RESET_PASSWORD);

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .required(t('forms.confirmPassword.error.required'))
      .test({
        name: 'invalid-chars',
        message: ({ value }) => getInvalidCharsMessage(value) || '',
        test: (value) => {
          if (!value) return true;
          return !getInvalidCharsMessage(value);
        },
      })
      .matches(
        /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[¡!#$%&()*+,\-./:;<=>¿?@[\]\\^_{|}~])[A-Za-z\d¡!#$%&()*+,\-./:;<=>¿?@[\]\\^_{|}~]{10,255}$/u,
        t('forms.confirmPassword.error.regexp')
      ),
    confirmedPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], t('forms.confirmPassword.error.match'))
      .required(t('forms.confirmPassword.error.required')),
  });

  return (
    <Wrap>
      <Formik
        initialValues={{
          password: '',
          confirmedPassword: '',
        }}
        validationSchema={validationSchema}
        onSubmit={(values: FormikValues) => {
          const variables = {
            token,
            password: values?.password,
          };
          resetPassword({ variables: { ...variables } })
            .then((res) => {
              if (res) {
                dispatch(
                  addNotificationActionCreator({
                    type: NotificationType.Success,
                    title: t('forms.confirmPassword.notification.success'),
                  })
                );
                history.push('/sign-in');
              }
              return res;
            })
            .catch(() => {
              dispatch(
                addNotificationActionCreator({
                  type: NotificationType.Error,
                  title: t('forms.confirmPassword.notification.error'),
                })
              );
            });
        }}
      >
        <Form>
          <H3>{t('forms.confirmPassword.caption')}</H3>
          <Paragraph size="small">{t('forms.confirmPassword.subCaption')}</Paragraph>
          <FieldsGroup>
            <PasswordField
              name="password"
              fieldProps={{ label: t('forms.confirmPassword.label.password'), isLoading: loading }}
              placeholder={t('forms.confirmPassword.placeholder.password')}
            />
            <PasswordField
              name="confirmedPassword"
              fieldProps={{ label: t('forms.confirmPassword.label.confirm'), isLoading: loading }}
              placeholder={t('forms.confirmPassword.placeholder.confirm')}
            />
          </FieldsGroup>
          <Button type="submit">{t('forms.confirmPassword.cta.submit')}</Button>
        </Form>
      </Formik>
    </Wrap>
  );
};

export default ConfirmPassword;
